import type { Book, Chapter, LibrarySource, ProgressFile, Track } from '../types';
import {
  driveStreamUrl,
  ensureServiceWorker,
  isControlled,
  serviceWorkerError,
} from '../lib/swBridge';
import { downloadFile, fetchRange, readAppData, writeAppData } from './client';
import { bookChapters } from '../lib/mediaInfo';
import { scanLibrary } from './scanner';

const ROOT_KEY = 'shelf.driveRoot';

/** Above this, buffering into memory is not a reasonable fallback. */
const MAX_BUFFERED_BYTES = 200 * 1024 * 1024;

let bufferedUrl: { trackId: string; url: string } | null = null;

/** Downloads a track and hands back an object URL, keeping only one alive so a
 *  long listening session cannot accumulate hundreds of megabytes. */
async function bufferToObjectUrl(trackId: string): Promise<string> {
  if (bufferedUrl?.trackId === trackId) return bufferedUrl.url;

  const blob = await downloadFile(trackId);
  if (bufferedUrl) URL.revokeObjectURL(bufferedUrl.url);
  bufferedUrl = { trackId, url: URL.createObjectURL(blob) };
  return bufferedUrl.url;
}

export interface DriveRoot {
  id: string;
  name: string;
}

export function loadSavedRoot(): DriveRoot | null {
  const raw = localStorage.getItem(ROOT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DriveRoot;
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
}

export function saveRoot(root: DriveRoot | null): void {
  if (root) localStorage.setItem(ROOT_KEY, JSON.stringify(root));
  else localStorage.removeItem(ROOT_KEY);
}

/** Google Drive as a library backend. Same interface as the demo source, so
 *  everything above it — player, chapters, progress, UI — is unchanged. */
export class DriveSource implements LibrarySource {
  readonly kind = 'drive' as const;
  readonly label: string;

  constructor(private readonly root: DriveRoot) {
    this.label = root.name;
  }

  async listBooks(): Promise<Book[]> {
    // Best-effort: playback still works without it, just less efficiently.
    await ensureServiceWorker();
    const { books } = await scanLibrary(this.root.id);
    return books;
  }

  /**
   * Normally a `/__drive/<id>` URL handled by the service worker, which streams
   * byte ranges on demand.
   *
   * Where no worker is available — an old browser, a non-secure origin, or an
   * embedded webview that blocks registration — this falls back to downloading
   * the file into a Blob. That is genuinely worse (no seeking until it lands,
   * and the whole thing sits in memory), so it is capped by size and reports
   * plainly rather than pretending to stream.
   */
  async trackUrl(track: Track): Promise<string> {
    if (isControlled()) return driveStreamUrl(track.id);

    if (track.sizeBytes > MAX_BUFFERED_BYTES) {
      const mb = Math.round(track.sizeBytes / 1024 / 1024);
      throw new Error(
        `Cannot stream this ${mb} MB file: the background streaming worker did not start, ` +
          `and the file is too large to load into memory. ` +
          `${serviceWorkerError() ?? 'Reload the page, and make sure the app is served over HTTPS or localhost.'}`,
      );
    }
    return bufferToObjectUrl(track.id);
  }

  /** Only through the streaming worker: the buffering fallback would download
   *  the whole file just to read its length. */
  metadataUrl(track: Track): string | null {
    return isControlled() ? driveStreamUrl(track.id) : null;
  }

  readRange(track: Track, start: number, end: number): Promise<ArrayBuffer> {
    return fetchRange(track.id, start, end);
  }

  /** Chapter marks embedded in the files (M4B, MP3, FLAC, Ogg), read once
   *  each and kept; files without any are a chapter each. */
  chaptersFor(book: Book): Promise<Chapter[]> {
    return bookChapters(book, (track, start, end) => this.readRange(track, start, end));
  }

  loadProgress(): Promise<ProgressFile | null> {
    return readAppData<ProgressFile>();
  }

  saveProgress(progress: ProgressFile): Promise<void> {
    return writeAppData(progress);
  }
}
